import { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Flame, Sparkles, BookOpen, Stamp, Milk, Nut, Wheat, ScrollText, Film, Clapperboard } from "lucide-react";
import { api } from "../api";
import { useSeo } from "../seo";
import { VegDot, OrderButton } from "../components";
import { CravingChips } from "../cravings";
import { addPick } from "../picks";
import { orderHandoff, money } from "../api";
import { useT, useLang, tCat, dishDesc } from "../i18n";
import { getMealPrefs } from "../SuggestedMeal";
import { ThemeToggle } from "../ThemeToggle";
import { Editable } from "../content";
import { DishImg } from "../DishImg";

export const ALLERGEN_ICONS = { dairy: Milk, nuts: Nut, gluten: Wheat };
const ALLERGEN_NAMES = { dairy: "dairy", nuts: "nuts", gluten: "gluten" };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AllergenBadges = ({ dish, avoid = [], size = 12 }) => {
  const list = dish.allergens || [];
  if (!list.length) return null;
  return (
    <span className="flex items-center gap-1">
      {list.map((a) => {
        const I = ALLERGEN_ICONS[a];
        if (!I) return null;
        const hit = avoid.includes(a);
        return (
          <span key={a} data-testid={`allergen-${a}-${dish.slug}`}
            title={hit ? `Contains ${ALLERGEN_NAMES[a]} — clashes with your table preferences` : `Contains ${ALLERGEN_NAMES[a]}`}
            className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${hit ? "border-[#E25822] bg-[#E25822]/15 text-[#E25822]" : "border-white/15 text-white/40"}`}>
            <I size={size} />
          </span>
        );
      })}
    </span>
  );
};

export const CATEGORIES = ["Popular", "Starters", "Chaat & Street Food", "Tandoor & Grill", "Chicken", "Lamb & Goat", "Vegetarian", "Rice & Biryani", "Breads", "Urban Express", "Desserts", "Drinks"];

const CAT_MAP = {
  "Starters & Appetizers": "Starters",
  "Tandoori & Grill": "Tandoor & Grill",
  "Stuffed Parathas": "Breads",
  "Chicken Curries": "Chicken",
  "Goat & Lamb Curries": "Lamb & Goat",
  "Vegetarian Curries": "Vegetarian",
  "Punjabi Thalis & Meals": "Urban Express",
  "Urban Turban Specials": "Urban Express",
  "Biryani": "Rice & Biryani",
};
const SLUG_MAP = {
  "tandoori-chicken-chaat": "Chaat & Street Food",
  "loaded-amritsari-kulcha": "Chaat & Street Food",
  "amritsari-kulcha-chole": "Chaat & Street Food",
  "butter-chicken-taco-cone": "Chaat & Street Food",
  "basmati-rice": "Rice & Biryani",
  "jeera-rice": "Rice & Biryani",
  "tandoori-roti": "Breads",
  "plain-naan": "Breads",
  "garlic-naan": "Breads",
  "masala-chai": "Drinks",
  "mango-lassi": "Drinks",
  "punjabi-chaas": "Drinks",
  "jalebi-with-rabri": "Desserts",
  "gulab-jamun": "Desserts",
  "phirni": "Desserts",
};
export const menuCategory = (dish) => SLUG_MAP[dish.slug] || CAT_MAP[dish.category] || null;

const ComboRow = ({ combo }) => (
  <div data-testid={`combo-${combo.external_id}`} className="rounded-xl bg-[#121212] border border-white/10 overflow-hidden">
    {combo.image ? <img src={combo.image} alt={combo.name} className="w-full h-36 object-cover" loading="lazy" /> : null}
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-xl font-semibold">{combo.name}</p>
        <p className="text-[#D4AF37] font-bold shrink-0">{money(combo.price)}</p>
      </div>
      {combo.description && <p className="text-sm text-white/60 mt-1">{combo.description}</p>}
      {(combo.item_names || combo.items || []).length > 0 && (
        <p className="text-xs text-white/40 mt-2">{(combo.item_names || combo.items).join(" · ")}</p>
      )}
    </div>
  </div>
);

const SpiceFlames = ({ n }) => (
  <span className="flex items-center gap-0.5" title={`Spice level ${n}/5`}>
    {[1, 2, 3].map((i) => <Flame key={i} size={12} className={i <= Math.ceil(n / 2) ? "text-[#E25822]" : "opacity-20"} />)}
  </span>
);

const MenuRow = ({ dish, avoid }) => {
  const t = useT();
  const { lang } = useLang();
  const [teasing, setTeasing] = useState(false);
  const teaseTimer = useRef(null);
  useEffect(() => () => clearTimeout(teaseTimer.current), []);
  const startTease = () => {
    if (!dish.has_clip) return;
    clearTimeout(teaseTimer.current);
    setTeasing(true);
    teaseTimer.current = setTimeout(() => setTeasing(false), 3000);
  };
  const stopTease = () => {
    clearTimeout(teaseTimer.current);
    setTeasing(false);
  };
  return (
  <div data-testid={`menu-row-${dish.slug}`} onPointerEnter={startTease} onPointerLeave={stopTease}
    className="rounded-2xl bg-[#121212] border border-white/10 p-3 flex gap-3">
    <Link to={`/experience/dish/${dish.slug}`} className="shrink-0 relative">
      <DishImg src={dish.image} alt={dish.name} name={dish.name} price={dish.price} className="w-24 h-24 rounded-xl object-cover" />
      {teasing && (
        <video data-testid={`clip-teaser-${dish.slug}`} src={`${BACKEND_URL}/api/experience/story-video/${dish.slug}`}
          className="absolute inset-0 w-24 h-24 rounded-xl object-cover"
          autoPlay muted loop playsInline preload="auto" onError={stopTease} />
      )}
      {dish.has_story && (
        <span data-testid={`story-badge-${dish.slug}`} title="This dish has a story"
          className="absolute -top-1.5 -left-1.5 flex items-center gap-1 bg-black/80 border border-[#D4AF37]/70 text-[#D4AF37] rounded-full px-1.5 py-0.5 text-[7px] font-black tracking-[0.15em]">
          <Stamp size={9} /> STORY
        </span>
      )}
      {dish.has_clip && (
        <span data-testid={`clip-badge-${dish.slug}`} title="Watch the cooking clip in this dish's story"
          className="absolute -bottom-1.5 -left-1.5 flex items-center gap-1 bg-black/80 border border-[#E25822]/70 text-[#E25822] rounded-full px-1.5 py-0.5 text-[7px] font-black tracking-[0.15em]">
          <Film size={9} /> CLIP
        </span>
      )}
    </Link>
    <div className="flex-1 min-w-0">
      <div className="flex items-start gap-2">
        <VegDot veg={dish.vegetarian} />
        <Link to={`/experience/dish/${dish.slug}`} className="flex-1">
          <p className="text-base font-bold leading-snug">{dish.name}</p>
        </Link>
        <span className="text-base font-black text-[#D4AF37] shrink-0">{money(dish.price)}</span>
      </div>
      <p className="text-sm text-white/60 leading-relaxed mt-1 line-clamp-2">{dishDesc(dish, lang)}</p>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <SpiceFlames n={dish.spice} />
        <AllergenBadges dish={dish} avoid={avoid} />
        {(dish.available === false || dish.coming_soon) ? (
          <span data-testid={`menu-unavailable-${dish.slug}`}
            className="h-11 px-4 rounded-full border border-[#E25822]/60 text-[#E25822] text-xs font-black tracking-widest flex items-center">
            {dish.coming_soon ? "COMING SOON" : "UNAVAILABLE TODAY"}</span>
        ) : (
          <button data-testid={`menu-add-${dish.slug}`} onClick={() => addPick(dish)}
            className="h-11 px-4 rounded-full bg-[#D4AF37] text-black text-xs font-bold tracking-widest">{t("menu.add")}</button>
        )}
        <Link data-testid={`menu-story-${dish.slug}`} to={`/experience/dish/${dish.slug}#story`}
          className="h-11 px-4 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-bold tracking-widest flex items-center gap-1.5"><BookOpen size={13} />{t("menu.viewStory")}</Link>
        {dish.available !== false && !dish.coming_soon && (
          <button data-testid={`menu-order-${dish.slug}`} onClick={() => { addPick(dish, 1, true); orderHandoff(undefined, "menu"); }}
            className="h-11 px-4 rounded-full border border-white/20 text-xs font-bold tracking-widest">{t("menu.order")}</button>
        )}
        {dish.cinematic && (
          <Link data-testid={`menu-cinematic-${dish.slug}`} to={`/experience/cinematic/${dish.slug}`}
            className="h-11 px-4 rounded-full bg-gradient-to-r from-[#2A1206] to-[#1C160A] border border-[#E25822]/60 text-[#F0C742] text-xs font-bold tracking-widest flex items-center gap-1.5">
            <Clapperboard size={13} /> EXPERIENCE IT
          </Link>
        )}
      </div>
    </div>
  </div>
  );
};

const ExtRow = ({ dish }) => {
  const t = useT();
  const [storyOpen, setStoryOpen] = useState(false);
  return (
    <div data-testid={`ext-row-${dish.pos_id}`} className="rounded-2xl bg-[#121212] border border-[#D4AF37]/25 p-3">
      <div className="flex gap-3">
        <DishImg src={dish.image} alt={dish.name} name={dish.name} price={dish.price} className="w-24 h-24 rounded-xl object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <VegDot veg={dish.vegetarian} />
            <p className="text-base font-bold leading-snug flex-1">{dish.name}</p>
            <span className="text-base font-black text-[#D4AF37] shrink-0">{money(dish.price)}</span>
          </div>
          <p className="text-sm text-white/60 leading-relaxed mt-1 line-clamp-2">{dish.description}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <SpiceFlames n={dish.spice} />
            <button data-testid={`ext-add-${dish.pos_id}`} onClick={() => addPick(dish)}
              className="h-11 px-4 rounded-full bg-[#D4AF37] text-black text-xs font-bold tracking-widest">{t("menu.add")}</button>
            {dish.story && (
              <button data-testid={`ext-story-btn-${dish.pos_id}`} onClick={() => setStoryOpen(!storyOpen)}
                className="h-11 px-4 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-bold tracking-widest flex items-center gap-1.5">
                <ScrollText size={13} /> {storyOpen ? "HIDE STORY" : "FOOD STORY"}
              </button>
            )}
          </div>
        </div>
      </div>
      {storyOpen && dish.story && (
        <p data-testid={`ext-story-open-${dish.pos_id}`} className="mt-3 pt-3 border-t border-[#D4AF37]/15 text-sm text-white/70 leading-relaxed whitespace-pre-line">
          {dish.story}
        </p>
      )}
    </div>
  );
};

export default function MenuPage() {
  const t = useT();
  const { lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [cat, setCat] = useState(params.get("cat") || "Popular");
  const [q, setQ] = useState("");
  const [flavors, setFlavors] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [extDishes, setExtDishes] = useState([]);
  const [extLoaded, setExtLoaded] = useState(false);
  const [posSrc, setPosSrc] = useState(null);
  const [combos, setCombos] = useState([]);
  const avoid = (getMealPrefs().allergens || []);

  const menuLd = useMemo(() => {
    if (!dishes.length) return null;
    const sections = {};
    dishes.forEach((d) => { (sections[d.category] = sections[d.category] || []).push(d); });
    return {
      "@context": "https://schema.org",
      "@type": "Menu",
      name: "Urban by VeerGi Menu",
      inLanguage: "en",
      hasMenuSection: Object.entries(sections).map(([name, items]) => ({
        "@type": "MenuSection",
        name,
        hasMenuItem: items.slice(0, 30).map((d) => ({
          "@type": "MenuItem",
          name: d.name,
          ...(d.description ? { description: d.description } : {}),
          url: `https://myurbanpassport.com/experience/dish/${d.slug}`,
        })),
      })),
    };
  }, [dishes]);
  useSeo({
    title: "Menu",
    description: "The full Punjabi menu at Urban by VeerGi — curries, biryani, tandoor breads, mithai and more, with the story behind every dish.",
    path: "/menu",
    jsonLd: menuLd,
  });

  useEffect(() => {
    api.get("/dishes").then((r) => setDishes(r.data));
    api.get("/ext-menu").then((r) => { setExtDishes(r.data); setExtLoaded(true); }).catch(() => setExtLoaded(true));
    api.get("/menu/source").then((r) => setPosSrc(r.data)).catch(() => {});
    api.get("/menu/combos").then((r) => setCombos(r.data || [])).catch(() => {});
  }, []);

  // after the first POS push, the POS category list IS the menu structure
  const posLive = Boolean(posSrc?.pos_live && (posSrc.categories || []).length);
  const catChips = posLive
    ? ["Popular", ...posSrc.categories, ...(combos.length ? ["Combos"] : [])]
    : [...CATEGORIES];

  // stale deep link: /menu?cat=Extended%20Menu with nothing toggled on falls back to the default view
  useEffect(() => {
    if (extLoaded && cat === "Extended Menu" && extDishes.length === 0) {
      setCat("Popular");
      setParams({});
    }
  }, [extLoaded, cat, extDishes.length, setParams]);

  const pickCat = (c) => { setCat(c); setParams(c === "Popular" ? {} : { cat: c }); };
  const toggleFlavor = (t) => setFlavors((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  let list = dishes;
  if (q.trim()) list = list.filter((d) => `${d.name} ${d.description}`.toLowerCase().includes(q.toLowerCase()));
  else if (cat === "Popular") list = list.filter((d) => d.featured);
  else if (cat === "Combos") list = [];
  else list = list.filter((d) => (posLive ? d.category === cat : menuCategory(d) === cat));
  if (flavors.length) list = list.filter((d) => flavors.every((f) => (d.tags || []).includes(f)));

  // linked Extended Menu dishes surface inside their category (and in search); unlinked ones live only in the Extended Menu section
  const extList = q.trim()
    ? extDishes.filter((d) => `${d.name} ${d.description}`.toLowerCase().includes(q.toLowerCase()))
    : cat !== "Extended Menu" && !flavors.length
      ? extDishes.filter((d) => d.linked_category === cat)
      : [];

  return (
    <div data-testid="menu-page" className="px-4 pt-5 pb-10 mx-auto w-full max-w-7xl xl:max-w-none xl:px-8 2xl:px-12">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Editable k="menu.kicker" label="Menu kicker" fallback="Urban by VeerGi" as="p"
            className="text-xs tracking-[0.3em] text-[#D4AF37] uppercase mb-1" />
          <Editable k="menu.title" label="Menu title" fallback={t("menu.title")} as="h1"
            className="font-display text-4xl font-bold tracking-tight mb-4" />
        </div>
        <ThemeToggle testId="theme-toggle-menu" />
      </div>
      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
        <input data-testid="menu-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("menu.search")}
          className="w-full h-12 bg-[#1A1A1A] border border-white/10 rounded-full pl-11 pr-4 text-base focus:outline-none focus:border-[#D4AF37]/60" />
      </div>
      <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-2">
        {[...catChips, ...(extDishes.length ? ["Extended Menu"] : [])].map((c) => (
          <button key={c} data-testid={`menu-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => pickCat(c)}
            className={`shrink-0 h-11 px-4 rounded-full text-sm font-bold border transition-colors ${cat === c && !q ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/70 border-white/10"}`}>
            {tCat(lang, c)}
          </button>
        ))}
      </div>
      <details className="mt-2 mb-4">
        <summary data-testid="menu-flavor-toggle" className="text-sm font-bold text-[#D4AF37] cursor-pointer py-2">{t("menu.flavour")} {flavors.length ? `(${flavors.length})` : ""}</summary>
        <div className="pt-2"><CravingChips selected={flavors} onToggle={toggleFlavor} testPrefix="menu-flavor" /></div>
      </details>
      <p data-testid="menu-count" className="text-sm text-white/50 mb-1">{q ? `${list.length + extList.length} — "${q}"` : cat === "Combos" ? `Combos · ${combos.length}` : cat === "Extended Menu" ? `Extended Menu · ${extDishes.length} ${extDishes.length === 1 ? t("menu.dishOne") : t("menu.dishes")}` : `${tCat(lang, cat)} · ${list.length + extList.length} ${list.length + extList.length === 1 ? t("menu.dishOne") : t("menu.dishes")}`}</p>
      <div data-testid="allergen-legend" className="flex items-center gap-3 mb-3 text-[10px] tracking-widest text-white/40 flex-wrap">
        <span className="flex items-center gap-1"><Milk size={11} /> DAIRY</span>
        <span className="flex items-center gap-1"><Nut size={11} /> NUTS</span>
        <span className="flex items-center gap-1"><Wheat size={11} /> GLUTEN</span>
        {avoid.length > 0 && <span className="text-[#E25822] font-bold">RED = YOUR TABLE'S ALLERGY</span>}
        <span className="normal-case tracking-normal">A guide only — please confirm allergies with your server.</span>
      </div>
      {cat === "Combos" && !q ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-4 2xl:grid-cols-5">
          {combos.map((c) => <ComboRow key={c.external_id} combo={c} />)}
        </div>
      ) : cat === "Extended Menu" && !q ? (
        <>
          <div data-testid="ext-menu-header" className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 mb-3">
            <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold">EXTENDED MENU · CHEF'S SPECIALS</p>
            <p className="text-[11px] text-white/50 mt-0.5">Chef's picks beyond our regular menu — ask your server about today's availability.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-4 2xl:grid-cols-5">
            {extDishes.map((d) => <ExtRow key={d.pos_id} dish={d} />)}
          </div>
          {extDishes.length === 0 && <p className="text-base text-white/60 py-8">{t("menu.empty")}</p>}
        </>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-4 2xl:grid-cols-5">
        {list.map((d) => <MenuRow key={d.slug} dish={d} avoid={avoid} />)}
        {extList.map((d) => <ExtRow key={d.pos_id} dish={d} />)}
      </div>
      )}
      {cat !== "Extended Menu" && cat !== "Combos" && list.length + extList.length === 0 && <p className="text-base text-white/60 py-8">{t("menu.empty")}</p>}
      <div className="xp-divider" />
      <Link to="/experience/veergi" data-testid="menu-ask-veergi" className="block rounded-2xl bg-[#121212] border border-[#D4AF37]/40 p-5 mt-2">
        <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><Sparkles size={16} /><span className="text-xs tracking-[0.3em] font-bold">{t("menu.askKicker")}</span></div>
        <p className="font-display text-2xl font-semibold">Ask VeerGi</p>
        <p className="text-sm text-white/60 mt-1">{t("menu.askDesc")}</p>
      </Link>
    </div>
  );
}
